// kfe-splash.js — the boot splash (Brief 121).
//
// PORT TARGET: iOS `iEnglish/LaunchScreen.xib` (Rule #9). That xib is four
// objects on a white root view:
//   • `LoginBackground` imageView pinned to all four edges, contentMode
//     scaleToFill — the orange sunburst (`SplashBg@2x/@3x`).
//   • the `KFE` imageView (the 功夫英语 lockup on black), centred on BOTH axes.
//   • two zero-background `LayoutLineView`s. These are invisible layout
//     scaffolding — they exist only to express the logo's width as
//     `100% − 2 × (54/320)` of the root view. Nothing paints them, so nothing
//     here renders them; their arithmetic is folded into LOGO_WIDTH_PCT below.
//
// GEOMETRY, taken from the xib rather than eyeballed (criterion 1):
//   • Root view 375×667 (`retina4_7`), backgroundColor pure white.
//   • Spacer width constraint `54:320` of the root width → 16.875% a side.
//   • Logo width = 100% − 2 × 16.875% = 66.25% of the viewport width.
//   • Logo aspect ratio constraint `750:248` — the exact pixel size of
//     `KFE@2x.png`, so the asset is displayed at its native proportion.
//   • Logo centreX = root centreX, centreY = root centreY.
//
// WHITE, DELIBERATELY. iOS's launch screen is white and the app chrome is
// dark; Brief 121 criterion 1 is explicit that this is NOT dark-scheme aware
// because iOS is not. Do not "fix" the contrast against the rest of the app.
//
// TWO DIVERGENCES, both where iOS canon is silent because iOS is portrait
// phone/iPad only and a browser viewport is any shape at all:
//   1. `object-fit: cover`, not iOS's `scaleToFill`. At 375×667 — the parity
//      width — the two are pixel-identical. scaleToFill genuinely stretches,
//      and iOS accepts that distortion across its own device range, but at a
//      16:9 desktop viewport it would be grotesque. Chris's call, 2026-11-02,
//      re-affirmed on review 2026-11-03: cover stands.
//   2. LOGO_MAX_WIDTH_PX caps the logo on wide viewports. 66.25% of a 1920px
//      window is a 1272px logo upscaled from a 750px asset. The cap is the
//      xib's own @1x intrinsic width for the `KFE` image (375), so it cannot
//      bite until the viewport passes 566px — every phone is exact.
//
// THE STATIC FIRST FRAME. This component is NOT the first thing the learner
// sees: `index.html` carries a hand-inlined `#kfe-boot-splash` div that paints
// the same two images before the JS bundle has even been fetched (criterion 3,
// and the brief's first gotcha — a 3G learner must see artwork, not white).
// This component takes over from it and removes it on mount. The two must stay
// visually identical or the handover flickers; the CSS below is duplicated
// verbatim in index.html's <style> block, and
// tests/components/kfe-splash.test.js pins the shared constants.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

/**
 * Where the splash artwork lives at runtime. Unbundled dev and `npm run dev`
 * (build.mjs --watch, which serves the PROJECT ROOT) read the source path;
 * a production build copies src/styles → dist/styles and serves /styles.
 * Mirrors how sw.js resolves __KFE_TOKENS_CSS__ (Brief 162): esbuild's
 * `define` injects it, and the typeof guard keeps the no-build path working.
 */
export const ASSET_BASE =
  // eslint-disable-next-line no-undef
  typeof __KFE_ASSET_BASE__ !== "undefined" ? __KFE_ASSET_BASE__ : "/src/styles";

/** Logo width as a percentage of the viewport — the xib's `54:320` spacers. */
export const LOGO_WIDTH_PCT = 66.25;
/** Logo aspect ratio — the xib's `750:248` constraint, i.e. the native size of the 2x logo asset. */
export const LOGO_ASPECT = "750 / 248";
/** Cap for wide viewports; the xib's 1x intrinsic width for the KFE image. */
export const LOGO_MAX_WIDTH_PX = 375;

export class KFESplash extends KFEComponent {
  /**
   * Shortest time the splash stays up, measured from NAVIGATION START rather
   * than from mount. The point is that the learner never sees a flash of
   * artwork — and what they perceive starts at first paint, which is the
   * static `#kfe-boot-splash` div, not this element. Measuring from mount
   * would stack 600 ms on top of however long the bundle took and make a slow
   * boot slower for no reason. `performance.now()` is already ms-since-
   * navigation-start, so `dismiss()` just compares against it.
   */
  static MIN_DISPLAY_MS = 600;

  /**
   * Boot is "taking a while" past this point and earns the indeterminate bar
   * (criterion 4 — iOS shows progress on this same backdrop during a content
   * update). No percentage: Brief 122 moved the heavy payload off the critical
   * path, so there is no meaningful number left to show.
   */
  static PROGRESS_AFTER_MS = 2000;

  static styles = `
    /* KEEP IN SYNC with the #kfe-boot-splash rules in index.html — the two
       render the same frame and any drift shows as a flicker at handover. */
    :host {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      display: block;
      background: #fff;           /* the xib's root-view backgroundColor */
      overflow: hidden;
    }
    :host([hidden]) { display: none; }
    img.bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;          /* iOS scaleToFill; see the docblock */
      object-position: center;
    }
    .center {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;    /* the xib's centreX + centreY constraints */
    }
    img.logo {
      display: block;
      width: ${LOGO_WIDTH_PCT}%;
      max-width: ${LOGO_MAX_WIDTH_PX}px;
      aspect-ratio: ${LOGO_ASPECT};
      height: auto;
    }
    /* Indeterminate bar, shown only past PROGRESS_AFTER_MS. Sits under the
       logo and is the same width, so it reads as part of the lockup rather
       than as browser chrome. */
    .progress {
      width: ${LOGO_WIDTH_PCT}%;
      max-width: ${LOGO_MAX_WIDTH_PX}px;
      height: 2px;
      margin-top: 1.25rem;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.15);
      border-radius: 1px;
    }
    .progress > span {
      display: block;
      width: 40%;
      height: 100%;
      background: rgba(0, 0, 0, 0.55);
      border-radius: 1px;
      animation: kfe-splash-slide 1.1s ease-in-out infinite;
    }
    @keyframes kfe-splash-slide {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(350%); }
    }
    /* Respect the OS setting — a looping bar is exactly the kind of motion
       prefers-reduced-motion is for. Static 100% bar instead of none at all,
       so the "still working" signal survives. */
    @media (prefers-reduced-motion: reduce) {
      .progress > span { width: 100%; animation: none; }
    }
  `;

  /** @type {boolean} */
  _showProgress = false;
  /** @type {any} */
  _progressTimer = null;

  onConnected() {
    // The two <img>s are alt="" (decorative), so without this the splash is
    // an unlabelled blocker to a screen reader. A literal rather than
    // t("app.title"): this element mounts before setLanguage() has resolved,
    // so t() would hand back the raw key — and the lockup itself carries both
    // the English and the 功夫英语 wordmark, so one brand string serves both.
    this.setAttribute("role", "img");
    this.setAttribute("aria-label", "Kungfu English");
    this.setAttribute("aria-busy", "true");

    // Hand over from the static first frame. One rAF so this element has
    // actually painted before the div underneath goes away — both draw the
    // same two images from the same URLs (already in the memory cache by now),
    // so the swap is invisible, but removing the div in the same frame as the
    // mount can expose a white gap on a slow paint.
    const handover = requestAnimationFrame(() => {
      document.getElementById("kfe-boot-splash")?.remove();
    });
    this.addCleanup(() => cancelAnimationFrame(handover));

    // Arm the indeterminate bar for whatever is LEFT of the 2 s budget — the
    // bundle has already eaten some of it before this element existed.
    const remaining = KFESplash.PROGRESS_AFTER_MS - performance.now();
    if (remaining <= 0) {
      this._showProgress = true;
      this._scheduleRender();
    } else {
      this._progressTimer = setTimeout(() => {
        this._progressTimer = null;
        this._showProgress = true;
        this._scheduleRender();
      }, remaining);
    }
    // Common Pitfall #2 — a timer that outlives the element would re-render a
    // detached component.
    this.addCleanup(() => this._clearProgressTimer());
  }

  _clearProgressTimer() {
    if (this._progressTimer !== null) {
      clearTimeout(this._progressTimer);
      this._progressTimer = null;
    }
  }

  /**
   * Take the splash down, honouring the minimum display. Resolves once the
   * element has been removed, so a caller can sequence against it.
   *
   * Idempotent: a second call returns the first call's promise rather than
   * queueing another removal.
   * @returns {Promise<void>}
   */
  dismiss() {
    if (this._dismissal) return this._dismissal;
    const wait = Math.max(0, KFESplash.MIN_DISPLAY_MS - performance.now());
    this._dismissal = new Promise((resolve) => {
      const finish = () => {
        this._clearProgressTimer();
        // Belt to the component's own braces: if this element was never
        // mounted (or was already pulled), the static div must still go, or
        // it would sit over the app forever.
        document.getElementById("kfe-boot-splash")?.remove();
        this.remove();
        resolve();
      };
      if (wait === 0) finish();
      else setTimeout(finish, wait);
    });
    return this._dismissal;
  }

  /** @type {Promise<void> | undefined} */
  _dismissal = undefined;

  render() {
    return html`
      <img class="bg" alt=""
           src="${ASSET_BASE}/splash-bg@2x.png"
           srcset="${ASSET_BASE}/splash-bg@2x.png 2x, ${ASSET_BASE}/splash-bg@3x.png 3x" />
      <div class="center">
        <img class="logo" alt=""
             src="${ASSET_BASE}/splash-logo@2x.png"
             srcset="${ASSET_BASE}/splash-logo@2x.png 2x" />
        ${this._showProgress
          ? html`<div class="progress" role="progressbar" aria-valuetext="Loading"><span></span></div>`
          : ""}
      </div>
    `;
  }
}
